import { ApplicationAcknowledgementStatus, CandidateState, DEFAULT_APPLICATION_ACKNOWLEDGEMENT_TEMPLATE, MessageTransport, ResponseExpectationAction, toUtcMs, } from './clientServerApi';
import { DEFAULT_CANDIDATE_CHANNELS_CONFIG, validateCandidateChannelsConfig } from './candidateChannels';
const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const MAX_TEMPLATE_LENGTH = 1000;
/**
 * Longest configurable follow-up delay: 4 weeks. Cloud Tasks schedules at most 30 days ahead, and
 * moving a due time into the sending window can add up to a day.
 */
export const MAX_FOLLOW_UP_DELAY_HOURS = 672;
const MINUTES_PER_DAY = 24 * 60;
function findUnknownVariable(template, knownNames) {
    const pattern = new RegExp(TEMPLATE_VARIABLE_PATTERN.source, 'g');
    let match;
    while ((match = pattern.exec(template)) !== null) {
        if (!knownNames.has(match[1]))
            return match[1];
    }
    return null;
}
function renderTemplateVariables(template, variables) {
    return template.replace(new RegExp(TEMPLATE_VARIABLE_PATTERN.source, 'g'), (_match, name) => variables[name] ?? '').trim();
}
export const APPLICATION_ACKNOWLEDGEMENT_VARIABLES = [
    { name: 'firstName', description: "Candidate's first name" },
    { name: 'recruiterName', description: "Recruiter's first name" },
    { name: 'company', description: 'Organization name' },
    { name: 'jobTitle', description: 'Job title' },
];
const VARIABLE_NAMES = new Set(APPLICATION_ACKNOWLEDGEMENT_VARIABLES.map((variable) => variable.name));
export function findUnknownApplicationAcknowledgementVariable(template) {
    return findUnknownVariable(template, VARIABLE_NAMES);
}
export function renderApplicationAcknowledgement(template, variables) {
    return renderTemplateVariables(template, { ...variables });
}
export function applicationAcknowledgementMessageId(candidateId, jobId) {
    return `application-ack-${candidateId}-${jobId}`;
}
/** Skip reasons the recruiter can clear (resume a paused candidate), so "Send now" is meaningful. */
const RETRYABLE_ACKNOWLEDGEMENT_SKIPS = new Set(['automation_paused']);
/** States before screening starts — afterwards a welcome text would arrive out of order. */
const PRE_SCREENING_STATES = new Set([
    CandidateState.Processing,
    CandidateState.Initialized,
    CandidateState.ResumeScored,
]);
/** Whether nothing beyond the welcome text has gone to the candidate yet: no screening question, no later stage. */
export function isAwaitingFirstContact(application) {
    return PRE_SCREENING_STATES.has(application.state) &&
        !(application.qualificationQuestions ?? []).some((question) => question.sentTimeUtc);
}
/** Whether the recruiter can (re)send a welcome text that failed or was held back. */
export function canRetryApplicationAcknowledgement(application) {
    const acknowledgement = application.applicationAcknowledgement;
    if (!acknowledgement || !isAwaitingFirstContact(application))
        return false;
    if (acknowledgement.status === ApplicationAcknowledgementStatus.Failed)
        return true;
    return acknowledgement.status === ApplicationAcknowledgementStatus.Skipped &&
        RETRYABLE_ACKNOWLEDGEMENT_SKIPS.has(acknowledgement.reason);
}
/** Stable id for the automatically sent first screening question, so a re-run task cannot send it twice. */
export function automaticQualificationMessageId(candidateId, jobId) {
    return `auto-qualification-${candidateId}-${jobId}`;
}
export const QUALIFICATION_AFTER_ACKNOWLEDGEMENT_INTRO = 'One quick question to help us review your application:';
/** The welcome text's opt-out line, e.g. "Reply STOP to opt out." */
const OPT_OUT_SENTENCE = /(?:Reply|Text|Txt|Send)\s+STOP\b[^.!?\n]*[.!?]?/i;
/** First qualification question merged into the application acknowledgement SMS; the opt-out line closes it. */
export function composeAcknowledgementWithQuestion(acknowledgementBody, questionText) {
    const optOut = OPT_OUT_SENTENCE.exec(acknowledgementBody);
    const welcome = optOut === null
        ? acknowledgementBody
        : `${acknowledgementBody.slice(0, optOut.index)}${acknowledgementBody.slice(optOut.index + optOut[0].length)}`
            .replace(/[ \t]{2,}/g, ' ')
            .trim();
    return [welcome, QUALIFICATION_AFTER_ACKNOWLEDGEMENT_INTRO, questionText, optOut === null ? '' : optOut[0].trim()]
        .filter((part) => part !== '')
        .join('\n\n');
}
/** First qualification question after the acknowledgement was already sent — no repeated welcome. */
export function composeQuestionAfterAcknowledgement(jobTitle, questionText) {
    return `One quick question to help us review your application for the "${jobTitle}" role:\n\n${questionText}`;
}
export const RESPONSE_FOLLOW_UP_VARIABLES = {
    [ResponseExpectationAction.QualificationAnswer]: [
        'firstName', 'recruiterName', 'company', 'jobTitle', 'qualificationQuestion',
    ],
    [ResponseExpectationAction.InterviewSelection]: ['firstName', 'recruiterName', 'company', 'jobTitle'],
    [ResponseExpectationAction.OfferResponse]: ['firstName', 'recruiterName', 'company', 'jobTitle'],
};
export const RESPONSE_FOLLOW_UP_ACTIONS = [
    {
        action: ResponseExpectationAction.QualificationAnswer,
        title: 'Screening questions',
        awaiting: 'a screening answer',
    },
    {
        action: ResponseExpectationAction.InterviewSelection,
        title: 'Interview time selection',
        awaiting: 'an interview time',
    },
    {
        action: ResponseExpectationAction.OfferResponse,
        title: 'Offer response',
        awaiting: 'an offer response',
    },
];
export function responseFollowUpActionLabel(action) {
    const match = RESPONSE_FOLLOW_UP_ACTIONS.find((entry) => entry.action === action);
    if (!match)
        throw new Error(`Unknown response follow-up action: ${action}`);
    return match;
}
export function findUnknownResponseFollowUpVariable(action, template) {
    return findUnknownVariable(template, new Set(RESPONSE_FOLLOW_UP_VARIABLES[action]));
}
export function renderResponseFollowUp(template, variables) {
    return renderTemplateVariables(template, { ...variables });
}
export function responseFollowUpMessageId(expectationId, attempt) {
    return `response-follow-up-${expectationId}-${attempt}`;
}
export const DEFAULT_RESPONSE_FOLLOW_UP_QUIET_HOURS = { earliest: '09:00', latest: '20:00' };
export const DEFAULT_RESPONSE_FOLLOW_UP_POLICIES = {
    [ResponseExpectationAction.QualificationAnswer]: {
        enabled: false,
        firstDelayHours: 24,
        finalDelayHours: 72,
        firstTemplate: 'Hi {{firstName}}, just checking that you saw our question about the {{jobTitle}} role. ' +
            'When you have a moment, please reply here: {{qualificationQuestion}}',
        finalTemplate: 'Hi {{firstName}}, one last check-in about your application for the {{jobTitle}} role. ' +
            'Reply here if you\'d still like to continue. We\'ll pause messages if we don\'t hear back.',
    },
    [ResponseExpectationAction.InterviewSelection]: {
        enabled: false,
        firstDelayHours: 4,
        finalDelayHours: 24,
        firstTemplate: 'Hi {{firstName}}, just checking whether any of the interview times we sent for the ' +
            '{{jobTitle}} role work for you. Reply with the time that suits you best, or suggest another time.',
        finalTemplate: 'Hi {{firstName}}, one last check-in about scheduling your {{jobTitle}} interview. ' +
            'Reply here if you\'d still like to meet. We\'ll pause messages if we don\'t hear back.',
    },
    [ResponseExpectationAction.OfferResponse]: {
        enabled: false,
        firstDelayHours: 24,
        finalDelayHours: 72,
        firstTemplate: 'Hi {{firstName}}, just checking that you saw our offer for the {{jobTitle}} role. ' +
            'Reply here with any questions, or let us know if you\'d like to accept.',
        finalTemplate: 'Hi {{firstName}}, one last check-in about our offer for the {{jobTitle}} role. ' +
            'Reply here when you\'re ready. We\'ll pause messages if we don\'t hear back.',
    },
};
const DEFAULT_QUALIFICATION_WORKFLOW = { mode: 'ask', scoreThreshold: 7 };
export function resolveCandidateCommunicationsConfig(stored) {
    const acknowledgement = stored?.applicationAcknowledgement;
    const qualification = stored?.qualification ?? DEFAULT_QUALIFICATION_WORKFLOW;
    const quietHours = stored?.noResponseFollowUps?.quietHours ?? DEFAULT_RESPONSE_FOLLOW_UP_QUIET_HOURS;
    const storedPolicies = stored?.noResponseFollowUps?.policies;
    const policy = (action) => ({
        ...(storedPolicies?.[action] ?? DEFAULT_RESPONSE_FOLLOW_UP_POLICIES[action]),
    });
    return {
        applicationAcknowledgement: acknowledgement
            ? { ...acknowledgement }
            : {
                enabled: false,
                transport: MessageTransport.Sms,
                bodyTemplate: DEFAULT_APPLICATION_ACKNOWLEDGEMENT_TEMPLATE,
            },
        qualification: { ...qualification },
        noResponseFollowUps: {
            quietHours: { ...quietHours },
            policies: {
                [ResponseExpectationAction.QualificationAnswer]: policy(ResponseExpectationAction.QualificationAnswer),
                [ResponseExpectationAction.InterviewSelection]: policy(ResponseExpectationAction.InterviewSelection),
                [ResponseExpectationAction.OfferResponse]: policy(ResponseExpectationAction.OfferResponse),
            },
            alternateChannel: stored?.noResponseFollowUps?.alternateChannel ?? false,
        },
        channels: { order: [...(stored?.channels?.order ?? DEFAULT_CANDIDATE_CHANNELS_CONFIG.order)] },
    };
}
export function parseClockMinutes(value) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}
/** Apply a settings update section by section; per-action follow-up policies merge individually. */
export function mergeCandidateCommunicationsConfig(current, update) {
    return resolveCandidateCommunicationsConfig({
        applicationAcknowledgement: update.applicationAcknowledgement ?? current.applicationAcknowledgement,
        qualification: update.qualification ?? current.qualification,
        noResponseFollowUps: {
            quietHours: update.noResponseFollowUps?.quietHours ?? current.noResponseFollowUps.quietHours,
            policies: { ...current.noResponseFollowUps.policies, ...update.noResponseFollowUps?.policies },
            alternateChannel: update.noResponseFollowUps?.alternateChannel ??
                current.noResponseFollowUps.alternateChannel,
        },
        channels: update.channels ?? current.channels,
    });
}
function validateTemplate(label, template, unknownVariable) {
    if (typeof template !== 'string' || !template.trim())
        return `${label} is required`;
    if (template.length > MAX_TEMPLATE_LENGTH)
        return `${label} must be ${MAX_TEMPLATE_LENGTH} characters or fewer`;
    if (unknownVariable)
        return `${label} uses an unknown variable: ${unknownVariable}`;
    return null;
}
/** Returns the first problem with the configuration, or null when it is valid. */
export function validateCandidateCommunicationsConfig(config) {
    return validateApplicationAcknowledgementConfig(config.applicationAcknowledgement) ??
        validateQualificationWorkflow(config.qualification) ??
        validateNoResponseFollowUpsConfig(config.noResponseFollowUps) ??
        validateCandidateChannelsConfig(config.channels);
}
/** The welcome text's problem (its switch, channel or message), or null when it is valid. */
export function validateApplicationAcknowledgementConfig(acknowledgement) {
    if (typeof acknowledgement.enabled !== 'boolean')
        return 'The welcome text switch is invalid';
    if (acknowledgement.transport !== MessageTransport.Sms)
        return 'Application acknowledgement must use SMS';
    return validateTemplate('Acknowledgement message', acknowledgement.bodyTemplate, findUnknownApplicationAcknowledgementVariable(acknowledgement.bodyTemplate ?? ''));
}
/** A valid welcome text as a job stores it: only its own fields, the message trimmed. */
export function storedApplicationAcknowledgement(acknowledgement) {
    return {
        enabled: acknowledgement.enabled,
        transport: MessageTransport.Sms,
        bodyTemplate: acknowledgement.bodyTemplate.trim(),
    };
}
/** Valid follow-ups as a job stores them: only their own fields, the messages trimmed. */
export function storedNoResponseFollowUps(config) {
    const policy = (action) => {
        const { enabled, firstDelayHours, finalDelayHours, firstTemplate, finalTemplate } = config.policies[action];
        return {
            enabled,
            firstDelayHours,
            finalDelayHours,
            firstTemplate: firstTemplate.trim(),
            finalTemplate: finalTemplate.trim(),
        };
    };
    return {
        quietHours: { earliest: config.quietHours.earliest, latest: config.quietHours.latest },
        policies: {
            [ResponseExpectationAction.QualificationAnswer]: policy(ResponseExpectationAction.QualificationAnswer),
            [ResponseExpectationAction.InterviewSelection]: policy(ResponseExpectationAction.InterviewSelection),
            [ResponseExpectationAction.OfferResponse]: policy(ResponseExpectationAction.OfferResponse),
        },
        alternateChannel: config.alternateChannel,
    };
}
/** The welcome text in effect for a job: its own when it has one, else its owner's workspace setting. */
export function resolveApplicationAcknowledgement(jobAcknowledgement, workspaceAcknowledgement) {
    return jobAcknowledgement ?? workspaceAcknowledgement;
}
/** The follow-ups in effect for a job: its own when it has them, else its owner's workspace setting. */
export function resolveNoResponseFollowUps(jobFollowUps, workspaceFollowUps) {
    if (!jobFollowUps)
        return workspaceFollowUps;
    // An action added after the job was customized follows the workspace until the job is saved again.
    return { ...jobFollowUps, policies: { ...workspaceFollowUps.policies, ...jobFollowUps.policies } };
}
export function validateQualificationWorkflow(workflow) {
    const { mode, scoreThreshold } = workflow;
    if (mode !== 'auto' && mode !== 'ask' && mode !== 'off')
        return 'Qualification mode is invalid';
    if (typeof scoreThreshold !== 'number' || !Number.isFinite(scoreThreshold) ||
        scoreThreshold < 0 || scoreThreshold > 10) {
        return 'Qualification score threshold must be between 0 and 10';
    }
    return null;
}
/**
 * The screening workflow in effect for a job. A job-level override applies only when it is complete:
 * older jobs can carry partial values from the retired per-job pause toggle (e.g. `{ mode: 'off' }`)
 * that must neither bypass the minimum score nor silently override the workspace setting.
 */
export function resolveQualificationWorkflow(jobWorkflow, workspaceWorkflow) {
    return jobWorkflow !== undefined && validateQualificationWorkflow(jobWorkflow) === null
        ? jobWorkflow
        : workspaceWorkflow;
}
export function validateNoResponseFollowUpsConfig(config) {
    const earliest = parseClockMinutes(config.quietHours.earliest);
    const latest = parseClockMinutes(config.quietHours.latest);
    if (earliest === null || latest === null)
        return 'Follow-up sending hours must use HH:MM';
    if (earliest >= latest)
        return 'Follow-up sending hours must end after they start';
    if (typeof config.alternateChannel !== 'boolean')
        return 'The other-channel follow-up switch is invalid';
    for (const { action, title } of RESPONSE_FOLLOW_UP_ACTIONS) {
        const policy = config.policies[action];
        if (!policy)
            return `${title} follow-up settings are missing`;
        if (typeof policy.enabled !== 'boolean')
            return `${title} follow-up switch is invalid`;
        const delays = [policy.firstDelayHours, policy.finalDelayHours];
        if (delays.some((hours) => !Number.isInteger(hours) || hours < 1 || hours > MAX_FOLLOW_UP_DELAY_HOURS)) {
            return `${title} follow-up delays must be whole hours from 1 to ${MAX_FOLLOW_UP_DELAY_HOURS}`;
        }
        if (policy.finalDelayHours <= policy.firstDelayHours) {
            return `${title} final follow-up must come after the first follow-up`;
        }
        const firstError = validateTemplate(`${title} first follow-up`, policy.firstTemplate, findUnknownResponseFollowUpVariable(action, policy.firstTemplate ?? ''));
        if (firstError)
            return firstError;
        const finalError = validateTemplate(`${title} final follow-up`, policy.finalTemplate, findUnknownResponseFollowUpVariable(action, policy.finalTemplate ?? ''));
        if (finalError)
            return finalError;
    }
    return null;
}
function localMinutesOfDay(timestampUtc, timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(new Date(timestampUtc));
    const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0') % 24;
    const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
    return hour * 60 + minute;
}
/**
 * Move a send time into the candidate-local sending window. Too early → that
 * morning's start; too late → the next morning's start. Inside the window → unchanged.
 */
export function nextAllowedSendTimeUtc(timestampUtc, quietHours, timezone) {
    const earliest = parseClockMinutes(quietHours.earliest);
    const latest = parseClockMinutes(quietHours.latest);
    if (earliest === null || latest === null || earliest >= latest)
        return toUtcMs(timestampUtc);
    let zone = timezone;
    try {
        localMinutesOfDay(timestampUtc, zone);
    }
    catch {
        zone = 'UTC';
    }
    const local = localMinutesOfDay(timestampUtc, zone);
    if (local >= earliest && local <= latest)
        return toUtcMs(timestampUtc);
    const minutesForward = local < earliest ? earliest - local : MINUTES_PER_DAY - local + earliest;
    let adjusted = timestampUtc - (timestampUtc % 60000) + minutesForward * 60000;
    // A DST change between the two instants shifts the local clock; correct once.
    const drift = localMinutesOfDay(adjusted, zone) - earliest;
    if (drift !== 0 && Math.abs(drift) <= 120)
        adjusted -= drift * 60000;
    return toUtcMs(adjusted);
}
/** Minutes after quiet hours end that sends moved past them are spread over. */
export const QUIET_HOURS_SPREAD_MINUTES = 45;
/**
 * `nextAllowedSendTimeUtc`, spread out: a send moved past quiet hours lands somewhere in the first
 * 45 minutes after they end — the same minute for the same key (a candidate), and within the allowed
 * hours — so a timezone's candidates aren't all nudged at the stroke of the hour.
 */
export function nextAllowedSendTimeSpreadUtc(timestampUtc, quietHours, timezone, spreadKey) {
    const allowed = nextAllowedSendTimeUtc(timestampUtc, quietHours, timezone);
    const earliest = parseClockMinutes(quietHours.earliest);
    const latest = parseClockMinutes(quietHours.latest);
    if (allowed === timestampUtc || earliest === null || latest === null)
        return allowed;
    // Over as many minutes as the allowed hours hold (their last included): capping the offset instead would put
    // every send past the end of short hours on their last minute.
    const offsetMinutes = stableHash(spreadKey) % Math.min(QUIET_HOURS_SPREAD_MINUTES, latest - earliest + 1);
    return toUtcMs(allowed + offsetMinutes * 60000);
}
/** FNV-1a: the same number for the same text, spread evenly. */
function stableHash(text) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash;
}
